<template>
  <div class="pivot-crop-area-wrapper">
    <div class="pivot-crop-area" ref="mapName"/>
  </div>
</template>

<script>
import computeDestinationPoint from 'geolib/es/computeDestinationPoint'
export default {
  name: 'pivotCropArea',
  props: {
    cropData: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      map: null,
      currentShape: null, // Reference to current polygon or circle
      resizeObserver: null
    }
  },
  mounted () {
    // Wait for next tick to ensure DOM is fully rendered
    this.$nextTick(() => {
      this.loadMap()
      this.setupResizeObserver()
    })
  },
  beforeDestroy () {
    // Clean up ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    // Clean up resize event listener if used as fallback
    window.removeEventListener('resize', this.handleResize)
  },
  watch: {
    'cropData.startAngle' () {
      this.renderShape()
    },
    'cropData.endAngle' () {
      this.renderShape()
    },
    'cropData.location': {
      handler () {
        if (this.cropData.location && this.cropData.location.latitude && this.cropData.location.longitude && this.cropData.radius) {
          this.setMapBounds(this.cropData.location.latitude, this.cropData.location.longitude, this.cropData.radius)
          this.renderShape()
        }
      },
      deep: true
    },
    'cropData.radius' () {
      if (this.cropData.location && this.cropData.location.latitude && this.cropData.location.longitude && this.cropData.radius) {
        this.setMapBounds(this.cropData.location.latitude, this.cropData.location.longitude, this.cropData.radius)
        this.renderShape()
      }
    }
  },
  methods: {
    loadMap () {
      const element = this.$refs.mapName
      const cropData = this.cropData
      const options = {
        zoom: 5,
        center: {lat: cropData.location.latitude, lng: cropData.location.longitude},
        mapTypeId: google.maps.MapTypeId.HYBRID,
        disableDefaultUI: true,
        zoomControl: false,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        gestureHandling: 'none',
        keyboardShortcuts: false,
        scrollwheel: false,
        draggable: false,
        disableDoubleClickZoom: true,
        mapId: 'pivot-crop-area'
      }
      this.map = new google.maps.Map(element, options)

      // Wait for map to be fully initialized before setting bounds
      google.maps.event.addListenerOnce(this.map, 'idle', () => {
        if (cropData.location && cropData.location.latitude && cropData.location.longitude && cropData.radius) {
          this.setMapBounds(cropData.location.latitude, cropData.location.longitude, cropData.radius)
        }
        this.renderShape()
      })
    },
    setupResizeObserver () {
      // Check if ResizeObserver is available
      if (typeof window === 'undefined' || !window.ResizeObserver) {
        // Fallback for browsers without ResizeObserver
        if (typeof window !== 'undefined') {
          window.addEventListener('resize', this.handleResize)
        }
        return
      }

      const element = this.$refs.mapName
      if (!element) return

      // Use window.ResizeObserver explicitly
      this.resizeObserver = new window.ResizeObserver(() => {
        this.handleResize()
      })
      this.resizeObserver.observe(element)
    },
    handleResize () {
      if (!this.map) return

      const cropData = this.cropData

      // Trigger Google Maps resize event
      google.maps.event.trigger(this.map, 'resize')

      // Reapply bounds based on center and radius after resize
      if (cropData.location && cropData.location.latitude && cropData.location.longitude && cropData.radius) {
        this.$nextTick(() => {
          this.setMapBounds(cropData.location.latitude, cropData.location.longitude, cropData.radius)
        })
      }
    },
    setMapBounds (latitude, longitude, radiusInMeters) {
      if (!this.map) return

      // Convert meters to degrees
      // 1 degree of latitude ≈ 111,320 meters
      const metersPerDegreeLat = 111320
      // 1 degree of longitude varies with latitude: 111,320 * cos(latitude)
      const metersPerDegreeLng = 111320 * Math.cos(latitude * Math.PI / 180)
      
      // Calculate the offset in degrees for the radius
      const latOffset = radiusInMeters / metersPerDegreeLat
      const lngOffset = radiusInMeters / metersPerDegreeLng
      
      // Create bounds that contain the circle based on center and radius
      const south = latitude - latOffset
      const north = latitude + latOffset
      const west = longitude - lngOffset
      const east = longitude + lngOffset
      
      const bounds = new google.maps.LatLngBounds(
        new google.maps.LatLng(south, west),
        new google.maps.LatLng(north, east)
      )
      
      // Fit bounds with minimal padding to reduce margins
      this.map.fitBounds(bounds, { top: 10, right: 10, bottom: 10, left: 10 })
    },
    clearShape () {
      if (this.currentShape) {
        this.currentShape.setMap(null)
        this.currentShape = null
      }
    },
    renderShape () {
      // Only render if map is initialized
      if (!this.map) {
        return
      }

      const cropData = this.cropData

      // Validate required data
      if (!cropData.location || !cropData.location.latitude || !cropData.location.longitude || 
          cropData.startAngle === undefined || cropData.endAngle === undefined || !cropData.radius) {
        this.clearShape()
        return
      }

      // Clear previous shape
      this.clearShape()

      // Draw new shape based on angles
      if (cropData.startAngle === cropData.endAngle) {
        this.currentShape = this.drawCircle(cropData.location, cropData.radius)
      } else {
        this.currentShape = this.drawSector(cropData.location, cropData.startAngle, cropData.endAngle, cropData.radius)
      }
    },
    drawSector (center, startAngleDeg, endAngleDeg, radiusMeters, options = {}) {
      // Validate required parameters
      if (!center || typeof startAngleDeg !== 'number' || typeof endAngleDeg !== 'number' || !radiusMeters) {
        return null
      }

      // Configuration constants
      const SEGMENT_COUNT = 60 // Number of segments for smooth arc rendering
      const styleOptions = this.getDefaultStyleOptions(options)

      // Helper function to generate arc points between two angles
      const generateArcPoints = (startAngle, endAngle, segmentCount) => {
        const angleRange = endAngle - startAngle
        const angleStep = angleRange / segmentCount
        const points = []

        for (let i = 0; i <= segmentCount; i++) {
          const currentAngle = startAngle + (angleStep * i)
          const arcPoint = computeDestinationPoint(
            center,
            radiusMeters,
            currentAngle // Degrees, measured from north clockwise
          )
          points.push(new google.maps.LatLng(arcPoint.latitude, arcPoint.longitude))
        }

        return points
      }

      // Build polygon path: start from center
      const paths = [new google.maps.LatLng(center.latitude, center.longitude)]

      // Check if sector crosses 0/360 degrees boundary
      if (startAngleDeg > endAngleDeg) {
        // Sector crosses 0/360: draw from startAngle to 360, then from 0 to endAngle
        const firstArcEnd = 360
        const secondArcStart = 0

        // First arc: from startAngle to 360
        const firstArcPoints = generateArcPoints(startAngleDeg, firstArcEnd, SEGMENT_COUNT)
        paths.push(...firstArcPoints)

        // Second arc: from 0 to endAngle
        const secondArcPoints = generateArcPoints(secondArcStart, endAngleDeg, SEGMENT_COUNT)
        paths.push(...secondArcPoints)
      } else {
        // Normal case: draw from startAngle to endAngle
        const arcPoints = generateArcPoints(startAngleDeg, endAngleDeg, SEGMENT_COUNT)
        paths.push(...arcPoints)
      }

      // Close polygon by returning to center
      paths.push(new google.maps.LatLng(center.latitude, center.longitude))

      // Create and return polygon with custom styling
      return new google.maps.Polygon({
        map: this.map,
        paths,
        ...styleOptions
      })
    },
    drawCircle (center, radiusMeters, options = {}) {
      // Validate required parameters
      if (!center || !radiusMeters) {
        return null
      }

      const styleOptions = this.getDefaultStyleOptions(options)

      // Create and return circle with custom styling (same as polygon)
      return new google.maps.Circle({
        map: this.map,
        center: new google.maps.LatLng(center.latitude, center.longitude),
        radius: radiusMeters,
        ...styleOptions
      })
    },
    getDefaultStyleOptions (options = {}) {
      // Shared style constants for consistency
      const DEFAULT_STROKE_COLOR = '#0098E1'
      const DEFAULT_FILL_COLOR = '#0098E1'
      const DEFAULT_STROKE_OPACITY = 1
      const DEFAULT_FILL_OPACITY = 0.5
      const DEFAULT_STROKE_WEIGHT = 3

      return {
        strokeColor: options.strokeColor || DEFAULT_STROKE_COLOR,
        strokeOpacity: options.strokeOpacity !== undefined ? options.strokeOpacity : DEFAULT_STROKE_OPACITY,
        strokeWeight: options.strokeWeight || DEFAULT_STROKE_WEIGHT,
        fillColor: options.fillColor || DEFAULT_FILL_COLOR,
        fillOpacity: options.fillOpacity !== undefined ? options.fillOpacity : DEFAULT_FILL_OPACITY,
        clickable: false,
        draggable: false,
        editable: false,
        visible: true,
        zIndex: 1
      }
    }
  }
}
</script>

<style scoped>
  .pivot-crop-area-wrapper {
    width: 100%;
    max-width: 100%;
    position: relative;
    display: block;
    overflow: hidden;
    /* Force 1:1 aspect ratio */
    aspect-ratio: 1 / 1;
  }

  /* Fallback for browsers without aspect-ratio: use padding-bottom trick */
  @supports not (aspect-ratio: 1 / 1) {
    .pivot-crop-area-wrapper {
      aspect-ratio: none;
    }

    .pivot-crop-area-wrapper::before {
      content: '';
      display: block;
      padding-bottom: 100%; /* Creates 1:1 aspect ratio */
      width: 100%;
    }
  }

  .pivot-crop-area {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    box-sizing: border-box;
    overflow: hidden;
  }

  /* Ensure map container respects dimensions */
  .pivot-crop-area > div {
    width: 100% !important;
    height: 100% !important;
    max-width: 100% !important;
    max-height: 100% !important;
  }
</style>


// WEBPACK FOOTER //
// PivotCropArea.vue?32c85be4